// ExcelExport.js
import React from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import { Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const ExcelExport = ({ data, theme, fileName = 'export', onExport }) => {
    const formatDate = (date) =>
        new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

    const createWorkbook = (expenses) => {
        // Transform data for Excel
        const excelData = expenses.map((activity, index) => ({
            Day: `Day ${expenses.length - index}`,
            Date: formatDate(activity.activityDate),
            Breakfast: activity.breakfast || 0,
            Lunch: activity.lunch || 0,
            Dinner: activity.dinner || 0,
            'Total Meals': (activity.breakfast || 0) + (activity.lunch || 0) + (activity.dinner || 0),
            Accommodation: activity.accommodation || 0,
            Incidental: activity.incidental || 0,
            Miscellaneous: activity.miscellaneous || 0,
            'Total Additional': (activity.incidental || 0) + (activity.miscellaneous || 0),
            'Daily Total':
                (activity.breakfast || 0) +
                (activity.lunch || 0) +
                (activity.dinner || 0) +
                (activity.accommodation || 0) +
                (activity.incidental || 0) +
                (activity.miscellaneous || 0)
        }));

        // Calculate totals
        const totals = excelData.reduce((acc, curr) => ({
            Day: 'TOTAL',
            Date: '',
            Breakfast: (acc.Breakfast || 0) + (curr.Breakfast || 0),
            Lunch: (acc.Lunch || 0) + (curr.Lunch || 0),
            Dinner: (acc.Dinner || 0) + (curr.Dinner || 0),
            'Total Meals': (acc['Total Meals'] || 0) + (curr['Total Meals'] || 0),
            Accommodation: (acc.Accommodation || 0) + (curr.Accommodation || 0),
            Incidental: (acc.Incidental || 0) + (curr.Incidental || 0),
            Miscellaneous: (acc.Miscellaneous || 0) + (curr.Miscellaneous || 0),
            'Total Additional': (acc['Total Additional'] || 0) + (curr['Total Additional'] || 0),
            'Daily Total': (acc['Daily Total'] || 0) + (curr['Daily Total'] || 0)
        }));

        excelData.push(totals);
        return excelData;
    };

    const applyStyles = (worksheet) => {
        const styleConfig = {
            headerStyle: {
                font: { bold: true, color: { rgb: 'FFFFFF' } },
                fill: { fgColor: { rgb: '2E7D32' } },
                alignment: { horizontal: 'center', vertical: 'center' },
                border: {
                    top: { style: 'thin' },
                    bottom: { style: 'thin' },
                    left: { style: 'thin' },
                    right: { style: 'thin' }
                }
            },
            totalRowStyle: {
                font: { bold: true },
                fill: { fgColor: { rgb: 'E8F5E9' } },
                alignment: { horizontal: 'right' },
                border: {
                    top: { style: 'double' },
                    bottom: { style: 'double' }
                }
            },
            currencyStyle: {
                numFmt: '$#,##0.00'
            }
        };

        const range = XLSX.utils.decode_range(worksheet['!ref']);

        // Apply header styles
        Object.keys(worksheet).forEach((cell) => {
            if (cell[0] === '!') return;
            const cellRef = XLSX.utils.decode_cell(cell);
            if (cellRef.r === 0) {
                worksheet[cell].s = styleConfig.headerStyle;
            } else if (cellRef.r === range.e.r) {
                worksheet[cell].s = { ...styleConfig.currencyStyle, ...styleConfig.totalRowStyle };
            } else if (cellRef.c >= 2) {
                worksheet[cell].s = styleConfig.currencyStyle;
            }
        });

        return worksheet;
    };

    const handleExport = () => {
        const workbookData = createWorkbook(data);
        const worksheet = XLSX.utils.json_to_sheet(workbookData);

        // Set column widths
        worksheet['!cols'] = [
            { wch: 10 }, // Day
            { wch: 25 }, // Date
            { wch: 12 }, // Breakfast
            { wch: 12 }, // Lunch
            { wch: 12 }, // Dinner
            { wch: 15 }, // Total Meals
            { wch: 15 }, // Accommodation
            { wch: 12 }, // Incidental
            { wch: 15 }, // Miscellaneous
            { wch: 18 }, // Total Additional
            { wch: 15 } // Daily Total
        ];

        // Apply styles
        const styledWorksheet = applyStyles(worksheet);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, styledWorksheet, 'Trip Expenses');

        // Set print options
        worksheet['!print'] = {
            gridLines: true,
            horizontalCentered: true,
            verticalCentered: false,
            headerFooter: {
                header: '&CTrip Expenses Summary'
            }
        };

        // Save file
        const date = new Date().toISOString().split('T')[0];
        const fullFileName = `${fileName}_${date}.xlsx`;
        XLSX.writeFile(workbook, fullFileName);

        // Callback if provided
        if (onExport) {
            onExport(fullFileName);
        }
    };

    return (
        <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            sx={{
                color: theme?.primary?.main || 'primary.main',
                borderColor: theme?.primary?.main || 'primary.main',
                '&:hover': {
                    borderColor: theme?.primary?.dark || 'primary.dark',
                    backgroundColor: 'rgba(0, 128, 0, 0.04)'
                }
            }}
        >
            Export to Excel
        </Button>
    );
};

ExcelExport.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            activityDate: PropTypes.string.isRequired,
            breakfast: PropTypes.number,
            lunch: PropTypes.number,
            dinner: PropTypes.number,
            accommodation: PropTypes.number,
            incidental: PropTypes.number,
            miscellaneous: PropTypes.number
        })
    ).isRequired,
    theme: PropTypes.object,
    fileName: PropTypes.string,
    onExport: PropTypes.func
};

export default ExcelExport;
